import { useMemo, useCallback } from 'react';
import { pick, compareObjects } from 'js/utils/ramda.util';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { periods } from '../config';
import * as helpers from '../helpers';
import { useValidate } from './useValidate';
import { ANOTHER_PERIOD_VALUE } from '../constants';
import { useDynamicReducerSelector } from '../store';
import { useExportControl } from './useExportControl';
import { useOnAnotherPeriodApply } from './useOnAnotherPeriodApply';
import { useOnPeriodFilterChange } from './useOnPeriodFilterChange';

export const useTableHeader = () => {
    const headerValidate = useValidate();
    const translate = useComponentTranslations();
    const afterFiltersControls = useExportControl();
    const onPeriodChange = useOnPeriodFilterChange();
    const onAnotherPeriodApply = useOnAnotherPeriodApply();
    const { period, startDate, endDate, isLoading } = useDynamicReducerSelector(pick([
        'period', 'startDate', 'endDate', 'isLoading'
    ]));

    const periodOptions = useMemo(() => periods.map(option => ({
        ...option, label: translate(`filter.periods.${option.label}`)
    })), [translate]);

    const headerInitialValues = useMemo(() => ({
        period,
        from: startDate,
        until: endDate
    }), [period, startDate, endDate]);

    const getIsDisabled = useCallback(({ from, until }) => isLoading
        || compareObjects(startDate, from)
        && compareObjects(endDate, until)
        || compareObjects(startDate, helpers.intoDate(from))
        && compareObjects(endDate, helpers.intoDate(until))
        || compareObjects(helpers.startOfDay(startDate), helpers.startOfDay(from))
        && compareObjects(helpers.startOfDay(endDate), helpers.startOfDay(until)),
        [endDate, isLoading, startDate]);

    const anotherPeriodFilter = useMemo(() => {
        return period === ANOTHER_PERIOD_VALUE ? {
            from: { label: translate('filter.from'), disabled: isLoading },
            until: { label: translate('filter.until'), disabled: isLoading },
            submit: {
                title: translate('filter.btn.apply'),
                getIsDisabled
            }
        } : null;
    }, [translate, getIsDisabled, period, isLoading]);

    return useMemo(() => ({
        filters: [{
            id: 0,
            name: 'period',
            disabled: isLoading,
            options: periodOptions,
            onChange: onPeriodChange,
            label: translate('filter.title.period'),
            'aria-label': translate(['filter.title.period', 'playtech.mobile.system.select.aria-label'])
        }],
        headerValidate,
        headerInitialValues,
        anotherPeriodFilter,
        onAnotherPeriodApply,
        afterFiltersControls
    }), [
        isLoading, translate, onAnotherPeriodApply, onPeriodChange, afterFiltersControls,
        headerValidate, headerInitialValues, anotherPeriodFilter, periodOptions
    ]);
};
